import { V2HexBackground } from "./v2/V2HexBackground";

/**
 * PageBackground — the single source of truth for the fixed-position backdrop
 * that sits behind page content (z-index: -1). Before PR-D the only backdrop
 * was a bare <V2HexBackground/> mounted once in App.tsx; auth and media
 * surfaces each rolled their own ad-hoc treatment, so the app surface drifted
 * page to page. This wraps the three canonical variants so every page reads
 * from the same component:
 *
 *   - "app"   the default in-app surface: the near-invisible hex grid over the
 *             theme `--background` token. Mounted once globally in App.tsx.
 *   - "auth"  the dark brand landing surface (Welcome / Login / Signup). These
 *             pages already force dark via SessionProvider; the hex backdrop is
 *             intentionally absent so the aurora-stage hero owns the look.
 *   - "media" full-bleed capture / scanner surfaces that paint their own
 *             camera feed or artwork: render no decorative layer so nothing
 *             tints the media underneath.
 *
 * Variants other than "app" render no decorative layer today — the distinction
 * is encoded so callers declare intent and future treatments have one place to
 * live, rather than re-deriving "should this page show the hex grid?".
 */
export type PageBackgroundVariant = "auth" | "app" | "media";

export function PageBackground({ variant = "app" }: { variant?: PageBackgroundVariant }) {
  if (variant === "app") return <V2HexBackground />;
  // auth + media intentionally render no shared decorative layer.
  return null;
}
